import { useQuery } from '@tanstack/react-query'
import { ModuleHeader, SpinnerWithBackground, TabBar } from '@viapeople/components'
import { useEffect } from 'react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useFeatureFlag } from '../../hooks/useFeatureFlag'
import { trpc } from '../../utils/trpc'
import { useCycleStore } from './store'

const tabs = [
	{ id: 'general', label: 'General' },
	{ id: 'admins', label: 'Admins' },
	{ id: 'viewers', label: 'Viewers' },
	{ id: 'participants', label: 'Self Evaluators' },
	{ id: 'evaluations', label: 'Evaluations' },
	{ id: 'roles', label: 'Form Roles' },
	{ id: 'rolesPermissions', label: 'Roles Permissions' },
	{ id: 'hierarchies', label: 'Hierarchies' },
	{ id: 'reviewers', label: 'Raters' },
	{ id: 'cycleSteps', label: 'Cycle Steps' },
	{ id: 'manageHolds', label: 'Manage Holds' },
	{ id: 'emails', label: 'Emails' },
	{ id: 'aiSummaries', label: 'AI Summaries' },
	{ id: 'buildCycle', label: 'Build Cycle' },
	{ id: 'reportFilters', label: 'Report Filters' },
	{ id: 'launchCycle', label: 'Launch Cycle' },
]

export const ManageCyclePage = () => {
	const { organizationId, cycleId } = useParams<{ organizationId: string; cycleId: string }>()
	const { data: organization, isLoading: isLoadingOrg } = useQuery({
		...trpc.admin.organizations.getById.queryOptions({ id: organizationId as string }),
		enabled: !!organizationId,
	})

	const { data: cycleData, isLoading: isLoadingCycle } = useQuery({
		...trpc.admin.cycles.getById.queryOptions({ id: cycleId as string }),
		enabled: !!cycleId,
	})

	const navigate = useNavigate()
	const location = useLocation()
	const pathArray = location.pathname.split('/')
	const currentTab = pathArray?.[5] ?? 'general'

	// Soft-delete viewing surface (Evaluations tab) is gated per-org. Hide the tab
	// from the nav when the flag is off; the route also early-returns at the page
	// level so deep links degrade gracefully.
	const { isEnabled: softDeleteEnabled } = useFeatureFlag('participant-soft-delete')
	const visibleTabs = softDeleteEnabled ? tabs : tabs.filter((t) => t.id !== 'evaluations')

	const { cycle, setCycle } = useCycleStore()

	useEffect(() => {
		if (cycleData) {
			setCycle(cycleData)
		} else if (organizationId) {
			setCycle({
				id: '',
				name: '',
				deadline: null,
				launchDate: null,
				previewDate: null,
				status: 'draft',
				hierarchyFileId: null,
				organizationId,
				isTemplate: false,
			})
		}
	}, [cycleData, organizationId, setCycle])

	const handleTabChange = (tabId: string) => {
		navigate(`/organizations/${organizationId}/cycles/${cycleId}/${tabId}`)
	}

	if (isLoadingOrg || isLoadingCycle) {
		return (
			<main>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</main>
		)
	}

	return (
		<main data-testid='cycle-manage-page' className='w-full p-6 min-h-full'>
			<div className='gap-6 flex flex-col'>
				<ModuleHeader
					breadcrumb={[
						{ label: 'Organizations', to: '/organizations' },
						{ label: organization?.name || '', to: `/organizations/${organizationId}` },
						{ label: 'Cycles', to: `/organizations/${organizationId}/cycles` },
						{ label: cycle?.name || (cycleId ? 'Manage Cycle' : 'Create Cycle') },
					]}
					title={cycle?.name || (cycleId ? 'Manage Cycle' : 'Create Cycle')}
					logo={{
						url: organization?.logo || '',
						name: organization?.name || '',
					}}
				/>
				<TabBar tabs={cycleId ? visibleTabs : [visibleTabs[0]]} activeTab={currentTab} onChange={handleTabChange} />
			</div>
			<Outlet context={{ organization, cycle }} />
		</main>
	)
}
